import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/DataTable';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { useAdminList } from '@/lib/useAdminList';
import { ListFooter } from '@/components/ListFooter';
import { FILTERABLE_PLATFORMS, PlatformBadge, PlatformList, platformMeta } from '@/lib/platform';
import { Search } from 'lucide-react';

interface Row {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  telegramId: string | null;
  isAdmin: boolean;
  avatarUrl: string | null;
  createdAt: string;
  activityCount: number;
  signupPlatform: string | null;
  /** Платформы, с которых заходил; первая — самая свежая. */
  seenPlatforms: string[] | null;
  lastSeenAt: string | null;
}

export function UsersListPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [isAdminFilter, setIsAdminFilter] = useState<'all' | 'true' | 'false'>('all');
  const [signupPlatform, setSignupPlatform] = useState('all');
  const [seenPlatform, setSeenPlatform] = useState('all');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const {
    rows, total, loaded, isLoading,
    hasNextPage, isFetchingNextPage, fetchNextPage,
  } = useAdminList<Row>(
    ['admin', 'users', debounced, isAdminFilter, signupPlatform, seenPlatform],
    '/api/admin/users',
    {
      q: debounced,
      is_admin: isAdminFilter === 'all' ? undefined : isAdminFilter,
      signup_platform: signupPlatform === 'all' ? undefined : signupPlatform,
      seen_platform: seenPlatform === 'all' ? undefined : seenPlatform,
    },
  );

  const columns: ColumnDef<Row>[] = [
    {
      accessorKey: 'email',
      header: 'Email',
      cell: (c) => {
        const v = c.getValue() as string | null;
        return v ? <span className="text-zinc-200">{v}</span> : <span className="text-zinc-600">—</span>;
      },
    },
    {
      id: 'name',
      header: 'Имя',
      cell: ({ row }) => {
        const r = row.original;
        const name = [r.firstName, r.lastName].filter(Boolean).join(' ');
        return name ? name : <span className="text-zinc-600">—</span>;
      },
    },
    {
      accessorKey: 'username',
      header: 'Username',
      cell: (c) => {
        const v = c.getValue() as string | null;
        return v ? <span className="font-mono text-xs text-zinc-300">@{v}</span> : <span className="text-zinc-600">—</span>;
      },
    },
    {
      accessorKey: 'telegramId',
      header: 'Telegram',
      cell: (c) => {
        const v = c.getValue() as string | null;
        return v ? <span className="font-mono text-xs text-zinc-400">{v}</span> : <span className="text-zinc-600">—</span>;
      },
    },
    {
      accessorKey: 'isAdmin',
      header: 'Admin',
      cell: (c) => (c.getValue() ? <Badge variant="default">admin</Badge> : null),
    },
    {
      accessorKey: 'signupPlatform',
      header: 'Регистрация',
      cell: (c) => {
        const v = c.getValue() as string | null;
        // NULL — аккаунт старше учёта источника, а не «не определили».
        return v ? <PlatformBadge platform={v} /> : <PlatformBadge platform="legacy" />;
      },
    },
    {
      id: 'seenPlatforms',
      header: 'Заходит с',
      cell: ({ row }) => <PlatformList platforms={row.original.seenPlatforms} />,
    },
    {
      accessorKey: 'lastSeenAt',
      header: 'Был',
      cell: (c) => {
        const v = c.getValue() as string | null;
        if (!v) return <span className="text-zinc-600">—</span>;
        return (
          <span className="font-mono text-xs tabular-nums text-zinc-400">
            {new Date(v).toLocaleDateString('ru')}
          </span>
        );
      },
    },
    {
      accessorKey: 'activityCount',
      header: 'Активити',
      cell: (c) => <span className="font-mono tabular-nums text-zinc-300">{c.getValue() as number}</span>,
    },
    {
      accessorKey: 'createdAt',
      header: 'Создано',
      cell: (c) => (
        <span className="font-mono text-xs tabular-nums text-zinc-500">
          {new Date(c.getValue() as string).toLocaleDateString('ru')}
        </span>
      ),
    },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-baseline gap-3">
          <h1 className="text-2xl font-semibold tracking-[-0.01em] text-zinc-50">Пользователи</h1>
          <span className="text-xs text-zinc-500">
            Найдено: <span className="font-mono tabular-nums text-zinc-300">{total}</span>
          </span>
        </div>
      </div>

      <div className="rounded-lg border border-zinc-800/80 bg-bg-2 p-3 flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-0 sm:max-w-md">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="email / имя / username / tg id…"
            className="pl-9"
          />
        </div>
        <Select
          value={isAdminFilter}
          onValueChange={(v) => setIsAdminFilter(v as 'all' | 'true' | 'false')}
        >
          <SelectTrigger className="w-full sm:w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все</SelectItem>
            <SelectItem value="true">Только админы</SelectItem>
            <SelectItem value="false">Не админы</SelectItem>
          </SelectContent>
        </Select>
        <PlatformSelect
          value={signupPlatform}
          onChange={setSignupPlatform}
          allLabel="Регистрация: любая"
          prefix="Рег."
        />
        <PlatformSelect
          value={seenPlatform}
          onChange={setSeenPlatform}
          allLabel="Заходит: откуда угодно"
          prefix="Заходит"
        />
      </div>

      {isLoading ? (
        <div className="text-zinc-500 text-sm">Загрузка…</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={rows}
            onRowClick={(r) => navigate({ to: '/users/$id', params: { id: r.id } })}
            empty="Пользователей не найдено"
          />
          <ListFooter
            loaded={loaded}
            total={total}
            hasMore={!!hasNextPage}
            isLoading={isFetchingNextPage}
            onMore={() => void fetchNextPage()}
          />
        </>
      )}
    </div>
  );
}

/** Фильтр по платформе — один и тот же для «где зарегался» и «откуда заходит». */
function PlatformSelect({
  value,
  onChange,
  allLabel,
  prefix,
}: {
  value: string;
  onChange: (v: string) => void;
  allLabel: string;
  prefix: string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full sm:w-[210px]"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{allLabel}</SelectItem>
        {FILTERABLE_PLATFORMS.map((p) => (
          <SelectItem key={p} value={p}>{prefix}: {platformMeta(p).label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
